import { Terminal } from '@xterm/xterm';
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';
import env from 'react-dotenv';

import { FrontendServerSchema, ServerUUID } from '@common/types';

type ConnectionStatus = 'connecting' | 'connected' | 'error' | 'disconnected';
type ConnectionPhase =
  | 'initializing'
  | 'connecting'
  | 'proxying'
  | 'hopping'
  | 'authenticating'
  | 'ready'
  | 'ready-hopping'
  | 'error';

interface ConnectionState {
  serverUUID: ServerUUID;
  serverData: FrontendServerSchema;
  websocket: WebSocket;
  scrollbackBuffer: string;
  status: ConnectionStatus;
  statusMessage: string;
  phase: ConnectionPhase;
  lastActivity: number;
}

interface OpenConnectionOptions {
  isHoppingEnabled?: boolean;
}

interface TerminalConnectionPoolContextType {
  connectionPool: Map<ServerUUID, ConnectionState>;
  activeConnectionUUID: ServerUUID | null;
  terminalRef: React.MutableRefObject<Terminal | null>;
  openConnection: (serverData: FrontendServerSchema, options?: OpenConnectionOptions) => void;
  minimizeConnection: () => void;
  switchToConnection: (serverUUID: ServerUUID) => void;
  closeConnection: (serverUUID: ServerUUID) => void;
  resumeConnection: (serverUUID: ServerUUID) => void; // Resume a minimized connection
  getActiveConnection: () => ConnectionState | null;
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  flushBufferToTerminal: () => void; // Flush accumulated buffer to terminal when ready
  isLoadingConnection: boolean; // Track if connection is being loaded/switched
}

const TerminalConnectionPoolContext = createContext<TerminalConnectionPoolContextType | undefined>(undefined);

export const useTerminalConnectionPool = () => {
  const context = useContext(TerminalConnectionPoolContext);
  if (!context) {
    throw new Error('useTerminalConnectionPool must be used within TerminalConnectionPoolProvider');
  }
  return context;
};

export const TerminalConnectionPoolProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [connectionPool, setConnectionPool] = useState<Map<ServerUUID, ConnectionState>>(new Map());
  const [activeConnectionUUID, setActiveConnectionUUID] = useState<ServerUUID | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoadingConnection, setIsLoadingConnection] = useState(false); // Track connection switching/loading
  const terminalRef = useRef<Terminal | null>(null);
  const activeConnectionUUIDRef = useRef<ServerUUID | null>(null);

  // Keep ref in sync with state
  React.useEffect(() => {
    activeConnectionUUIDRef.current = activeConnectionUUID;
  }, [activeConnectionUUID]);

  // Create or resume a connection
  const openConnection = useCallback(
    (serverData: FrontendServerSchema, options?: OpenConnectionOptions) => {
      const existingConnection = connectionPool.get(serverData.uuid);

      if (existingConnection) {
        // Resume existing connection
        setIsLoadingConnection(true); // Show loading state

        // Clear terminal first to remove any previous connection's data
        if (terminalRef.current) {
          terminalRef.current.clear();
        }

        setActiveConnectionUUID(serverData.uuid);
        activeConnectionUUIDRef.current = serverData.uuid; // Update ref immediately

        // Restore buffer to terminal after a brief delay to ensure clear happens first
        setTimeout(() => {
          if (terminalRef.current && existingConnection.scrollbackBuffer) {
            terminalRef.current.write(existingConnection.scrollbackBuffer);
          }
          setIsLoadingConnection(false);
        }, 50);

        setIsModalOpen(true);
        return;
      }

      // Create new connection
      // Clear terminal first to remove any previous connection's data
      if (terminalRef.current) {
        terminalRef.current.clear();
      }

      const token = localStorage.getItem('token') || '';
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = env.WSO_URL || `${wsProtocol}//${window.location.host}`;

      // Build WebSocket path with optional query parameter
      const wsPath = options?.isHoppingEnabled
        ? `/ws/ssh/${serverData.uuid}?isFailed=true`
        : `/ws/ssh/${serverData.uuid}`;

      const ws = new WebSocket(`${wsUrl}${wsPath}`, token);

      const newConnection: ConnectionState = {
        serverUUID: serverData.uuid,
        serverData,
        websocket: ws,
        scrollbackBuffer: '',
        status: 'connecting',
        statusMessage: 'Connecting...',
        phase: 'initializing',
        lastActivity: Date.now(),
      };

      // Setup WebSocket handlers
      ws.onmessage = (e: MessageEvent) => {
        const d = JSON.parse(e.data);

        setConnectionPool(prev => {
          const updated = new Map(prev);
          const conn = updated.get(serverData.uuid);
          if (!conn) return prev;

          switch (d.eventType) {
            case 'sshData':
              // Append to buffer
              conn.scrollbackBuffer += d.data;
              conn.lastActivity = Date.now();

              // Write to terminal if this is the active connection (use ref to avoid stale closure)
              if (activeConnectionUUIDRef.current === serverData.uuid && terminalRef.current) {
                terminalRef.current.write(d.data);
              }
              break;
            case 'success': {
              conn.status = 'connected';
              conn.statusMessage = d.data;
              // Check if this was a hopping connection
              const wasHopping = conn.phase === 'hopping' || conn.phase === 'proxying';
              conn.phase = wasHopping ? 'ready-hopping' : 'ready';
              conn.lastActivity = Date.now();
              // Clear loading state when connection is successful
              if (activeConnectionUUIDRef.current === serverData.uuid) {
                setIsLoadingConnection(false);
              }
              break;
            }
            case 'status':
              conn.statusMessage = d.data;
              conn.lastActivity = Date.now();
              // Update phase based on status message
              if (d.data.includes('Initiating') || d.data.includes('Step 1/3')) {
                conn.phase = 'initializing';
              } else if (d.data.includes('direct connection') || d.data.includes('Connecting to')) {
                conn.phase = 'connecting';
              } else if (d.data.includes('proxy') && !d.data.includes('tunnel')) {
                conn.phase = 'proxying';
              } else if (d.data.includes('tunnel') || d.data.includes('Step 3/3')) {
                conn.phase = 'hopping';
              } else if (d.data.includes('shell')) {
                conn.phase = 'authenticating';
              }
              break;
            case 'error':
              conn.status = 'error';
              conn.statusMessage = d.data;
              conn.phase = 'error';
              conn.lastActivity = Date.now();
              break;
          }

          updated.set(serverData.uuid, conn);
          return updated;
        });
      };

      ws.onclose = () => {
        setConnectionPool(prev => {
          const updated = new Map(prev);
          const conn = updated.get(serverData.uuid);
          if (!conn) return prev;

          conn.status = 'disconnected';
          conn.statusMessage = 'Not Connected';
          updated.set(serverData.uuid, conn);
          return updated;
        });
      };

      ws.onerror = () => {
        setConnectionPool(prev => {
          const updated = new Map(prev);
          const conn = updated.get(serverData.uuid);
          if (!conn) return prev;

          conn.status = 'error';
          conn.statusMessage = 'Connection Error';
          updated.set(serverData.uuid, conn);
          return updated;
        });
      };

      // Add to pool and set as active
      setConnectionPool(prev => new Map(prev).set(serverData.uuid, newConnection));
      setActiveConnectionUUID(serverData.uuid);
      activeConnectionUUIDRef.current = serverData.uuid; // Update ref immediately
      setIsLoadingConnection(true); // Show loading state for new connection
      setIsModalOpen(true);
    },
    [connectionPool, activeConnectionUUID]
  );

  // Flush accumulated buffer to terminal (called when terminal becomes ready)
  const flushBufferToTerminal = useCallback(() => {
    if (!activeConnectionUUID || !terminalRef.current) return;

    // Get fresh connection from pool (not using dependency to avoid recreating on every pool update)
    const connection = connectionPool.get(activeConnectionUUID);
    if (!connection) return;

    // Clear terminal first
    terminalRef.current.clear();

    // Write accumulated buffer to terminal if available
    if (connection.scrollbackBuffer) {
      terminalRef.current.write(connection.scrollbackBuffer);
    }

    // Clear loading state after flushing
    setIsLoadingConnection(false);
  }, [activeConnectionUUID]); // Only depend on activeConnectionUUID, read connectionPool fresh

  // Minimize current connection (keep alive, close modal)
  const minimizeConnection = useCallback(() => {
    if (!activeConnectionUUID) return;

    // No need to save buffer - we've been accumulating it in scrollbackBuffer all along
    // Just close the modal
    setIsModalOpen(false);
  }, [activeConnectionUUID]);

  // Switch to a different connection
  const switchToConnection = useCallback(
    (serverUUID: ServerUUID) => {
      const targetConnection = connectionPool.get(serverUUID);
      if (!targetConnection) return;

      // Show loading state during switch
      setIsLoadingConnection(true);

      // No need to save current buffer - we've been accumulating it in scrollbackBuffer all along

      // Clear terminal first to remove previous connection's data
      if (terminalRef.current) {
        terminalRef.current.clear();
      }

      setActiveConnectionUUID(serverUUID);
      activeConnectionUUIDRef.current = serverUUID; // Update ref immediately

      // Restore target connection's buffer after a brief delay
      setTimeout(() => {
        if (terminalRef.current && targetConnection.scrollbackBuffer) {
          terminalRef.current.write(targetConnection.scrollbackBuffer);
        }
        setIsLoadingConnection(false);
      }, 50);
    },
    [connectionPool]
  );

  // Close and cleanup a connection
  const closeConnection = useCallback(
    (serverUUID: ServerUUID) => {
      const connection = connectionPool.get(serverUUID);
      if (!connection) return;

      // Close WebSocket
      if (connection.websocket && connection.websocket.readyState === WebSocket.OPEN) {
        connection.websocket.close();
      }

      // Clear the connection's buffer
      connection.scrollbackBuffer = '';

      // Remove from pool
      setConnectionPool(prev => {
        const updated = new Map(prev);
        updated.delete(serverUUID);
        return updated;
      });

      // If this was the active connection, close modal, clear terminal and active UUID
      if (activeConnectionUUID === serverUUID) {
        setActiveConnectionUUID(null);
        activeConnectionUUIDRef.current = null; // Update ref immediately
        setIsModalOpen(false);
        setIsLoadingConnection(false); // Clear any loading state

        // Clear terminal completely
        if (terminalRef.current) {
          terminalRef.current.clear();
          terminalRef.current.reset(); // Reset terminal to initial state
        }
      }
    },
    [connectionPool, activeConnectionUUID]
  );

  // Get the current active connection
  const getActiveConnection = useCallback(() => {
    if (!activeConnectionUUID) return null;
    return connectionPool.get(activeConnectionUUID) || null;
  }, [activeConnectionUUID, connectionPool]);

  // Resume a minimized connection (wrapper around openConnection)
  const resumeConnection = useCallback(
    (serverUUID: ServerUUID) => {
      const connection = connectionPool.get(serverUUID);
      if (!connection) {
        console.warn(`Cannot resume connection: ${serverUUID} not found in pool`);
        return;
      }

      // Use openConnection with the existing server data
      // openConnection already handles resuming existing connections
      openConnection(connection.serverData);
    },
    [connectionPool, openConnection]
  );

  const value: TerminalConnectionPoolContextType = React.useMemo(
    () => ({
      connectionPool,
      activeConnectionUUID,
      terminalRef,
      openConnection,
      minimizeConnection,
      switchToConnection,
      closeConnection,
      resumeConnection,
      getActiveConnection,
      isModalOpen,
      setIsModalOpen,
      flushBufferToTerminal,
      isLoadingConnection,
    }),
    [
      connectionPool,
      activeConnectionUUID,
      terminalRef,
      openConnection,
      minimizeConnection,
      switchToConnection,
      closeConnection,
      resumeConnection,
      getActiveConnection,
      isModalOpen,
      setIsModalOpen,
      flushBufferToTerminal,
      isLoadingConnection,
    ]
  );

  return <TerminalConnectionPoolContext.Provider value={value}>{children}</TerminalConnectionPoolContext.Provider>;
};
